import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../../theme";

import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import MAddOrderField from "../../../../../../components/global/Modals/MAddOrderField";
import ButtonGroup from "../../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import ShDataGrid2 from "../../../../../../components/theme/datagrid2/ShDataGrid2";
import ShTextSkeleton from "../../../../../../components/theme/skeleton/ShTextSkeleton";
import SidepanelLoader from "../../../../../../components/theme/sidepanel2/SidepanelLoader";
import { selectUserPermission } from "../../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../../utils/permissions";
import { useSelector } from "react-redux";
import { generateColorFromTitle, generateIconColorFromTitle } from "../../../../../../components/theme/TabView3/IconColorGenerator";
import Text from "../../../../../../components/theme/text/Text";
import ProductExtraFieldsCard from "../../../../../../components/global/cards/settings/ProductExtraFieldsCard";
import { useGetOrderFieldsQuery } from "../../../../../../newapi/order/orderFieldSlice";
import { ClearIcon } from "@mui/x-date-pickers";
import { GridCheckIcon } from "@mui/x-data-grid";

const SpOrderValueDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpOrderValueDetails/SpOrderValueDetails"));

const GridActions = (props, i18n) => {
    const theme = useTheme();
    useEffect(() => {},[props.open]);

    const {t} = useTranslation();
    return (      
        <ButtonGroup version={2}>
            {props.hasCreatePermission && (
            <Shbutton 
                onClick={() => props.handleOpen(true)}
                className={"group-btn"} 
                color={theme.palette.mode === "dark" ? "blue" : "primary"} 
                variant="contained" 
                endIcon={<AddIcon/>}>
                    {t("new_field")}
            </Shbutton>
            )}
        </ButtonGroup>
    );
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const All = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isExtended,setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();

    const hasCreatePermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.PRODUCTFIELD_MANAGE])
    );

    const [config, setConfig] = useState({"all_credentials": true});

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
      };

    const [orderFieldId, setOrderFieldId] = useState();
    const orderFieldIdRef = useRef(orderFieldId);
    
    const getOrderFieldDetails = (params) => {
        if (!params) return;
    
        const newOrderFieldId = parseInt(params.row.id);
        // Only update state if the webshopCredentialsId has actually changed
        if (orderFieldIdRef.current !== newOrderFieldId) {
            if(!spLoading) {
                setSpLoading(true);
            }
            setOrderFieldId(newOrderFieldId);
            orderFieldIdRef.current = newOrderFieldId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions:gridOptions,
            mobileActions:<GridActions handleOpen={() => setAddModalOpen(true)} hasCreatePermission={hasCreatePermission}></GridActions>
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[setAddModalOpen,hasCreatePermission]);
    
    const columns = [
        {field: "name", headerName: t("name"),flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return <Text semibold>{params.row.name}</Text>
            }
        },
        {field: "valueType", headerName: t("value_type"),flex:1,
        renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }
            let backGround = generateColorFromTitle(params.row.valueType ?? "-", theme.palette.mode, 95)
            let foreGround = generateIconColorFromTitle(params.row.valueType ?? "-", theme.palette.mode)
            
            return (
                <Box
                    sx={{
                        height:32,
                        px:2,
                        display:"flex",
                        alignItems:'center',
                        fontFamily:"Roboto, sans-serif",
                        fontSize:14,
                        border:`1px solid ${foreGround}`,
                        color:foreGround,
                        background:backGround,
                        borderRadius:4,
                        fontWeight:500,
                    }}
                >
                    {t(params.row.valueType)}
                </Box>
                
            )
        }},
        {field: "hideForManualCreation", headerName: t("hide_for_manual_orders"),flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                return (
                    <Box>
                        <Text>
                            {params.row.hideForManualCreation ? (
                                <GridCheckIcon sx={{ color: 'green' }} />
                            ) : (
                                <ClearIcon sx={{ color: 'rgba(128, 128, 128, 0.5)' }} />
                            )}
                        </Text>
                    </Box>
                )
            }
        },
        {field: "listValues", headerName: t("list_value"),flex:2, sortable: false,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                if (params.row.valueType !== "list") {
                    return "";
                }
                const listValString = params.row.listValues?.join(", ");
                return listValString;
            }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <ProductExtraFieldsCard 
                        skeleton={params.row.skeleton ?? false}
                        onClick={() => getOrderFieldDetails(params)}
                        {...params.row}
                    />
                )
            }
        },
    ];

    return (
        <Box
            sx={{
                height:1,
                "@media screen and (max-width: 47.9375em)" : {
                    paddingTop:0,
                    paddingX:0,
                }
            }}
        >
            
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetOrderFieldsQuery}
                config={config}
                gridActions={<GridActions handleOpen={() => setAddModalOpen(true)} hasCreatePermission={hasCreatePermission}></GridActions>} 
                title={t("custom_fields")}
                gridOptions={gridOptions} 
                sortModel={{field: columns[0].field,type:"ASC"}}
                    
                columns={columns}
                onRowClick={getOrderFieldDetails}>
            </ShDataGrid2>


            <MAddOrderField onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose}/>


            {spLoading && 
                <SpOrderValueDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    orderFieldId={orderFieldId}
                    // data={credentialData}  
                /> 
            }
        </Box>
    )
};

export default All;